import { UserRole } from '../types';
import { getProjectsAccess } from './projectsAccess';

export enum Permission {
  // Ticket permissions
  CREATE_TICKET = 'CREATE_TICKET',
  VIEW_OWN_TICKETS = 'VIEW_OWN_TICKETS',
  VIEW_ALL_TICKETS = 'VIEW_ALL_TICKETS',
  UPDATE_OWN_TICKETS = 'UPDATE_OWN_TICKETS',
  UPDATE_ALL_TICKETS = 'UPDATE_ALL_TICKETS',
  DELETE_TICKETS = 'DELETE_TICKETS',
  ASSIGN_TICKETS = 'ASSIGN_TICKETS',
  APPROVE_TICKETS = 'APPROVE_TICKETS',
  REJECT_TICKETS = 'REJECT_TICKETS',
  
  // User permissions
  VIEW_USERS = 'VIEW_USERS',
  CREATE_USERS = 'CREATE_USERS',
  UPDATE_USERS = 'UPDATE_USERS',
  DELETE_USERS = 'DELETE_USERS',
  MANAGE_ROLES = 'MANAGE_ROLES',
  RESET_PASSWORDS = 'RESET_PASSWORDS',
  UPDATE_USERNAMES = 'UPDATE_USERNAMES',
  
  // Company permissions
  VIEW_COMPANIES = 'VIEW_COMPANIES',
  CREATE_COMPANIES = 'CREATE_COMPANIES',
  UPDATE_COMPANIES = 'UPDATE_COMPANIES',
  DELETE_COMPANIES = 'DELETE_COMPANIES',
  MANAGE_OWN_COMPANY = 'MANAGE_OWN_COMPANY',
  
  // Dashboard permissions
  VIEW_DASHBOARD = 'VIEW_DASHBOARD',
  VIEW_ALL_STATS = 'VIEW_ALL_STATS',
  VIEW_OWN_STATS = 'VIEW_OWN_STATS',
  
  // System permissions
  MANAGE_SYSTEM = 'MANAGE_SYSTEM',
  VIEW_AUDIT_LOGS = 'VIEW_AUDIT_LOGS',
  VIEW_TIME_TRACKING = 'VIEW_TIME_TRACKING',
  VIEW_COMPANY_USAGE = 'VIEW_COMPANY_USAGE',
  
  // Billing permissions
  VIEW_BILLING = 'VIEW_BILLING',
  MANAGE_BILLING = 'MANAGE_BILLING',
  VIEW_DEALS = 'VIEW_DEALS',

  // Projects module
  VIEW_PROJECTS = 'VIEW_PROJECTS',
  MANAGE_PROJECTS = 'MANAGE_PROJECTS'
}

// Define permissions for each role
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.SUPER_ADMIN]: [
    ...Object.values(Permission)
  ],
  
  [UserRole.AGENT]: [
    Permission.CREATE_TICKET,
    Permission.VIEW_OWN_TICKETS,
    Permission.VIEW_ALL_TICKETS,
    Permission.UPDATE_OWN_TICKETS,
    Permission.UPDATE_ALL_TICKETS,
    Permission.ASSIGN_TICKETS,
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ALL_STATS,
    Permission.VIEW_USERS,
    Permission.UPDATE_USERNAMES,
    Permission.VIEW_PROJECTS
  ],
  
  [UserRole.CLIENT_ADMIN]: [
    Permission.CREATE_TICKET,
    Permission.VIEW_OWN_TICKETS,
    Permission.VIEW_ALL_TICKETS,
    Permission.UPDATE_OWN_TICKETS,
    Permission.UPDATE_ALL_TICKETS,
    Permission.DELETE_TICKETS,
    Permission.APPROVE_TICKETS,
    Permission.REJECT_TICKETS,
    Permission.VIEW_USERS,
    Permission.CREATE_USERS,
    Permission.UPDATE_USERS,
    Permission.DELETE_USERS,
    Permission.MANAGE_ROLES,
    Permission.RESET_PASSWORDS,
    Permission.UPDATE_USERNAMES,
    Permission.VIEW_COMPANIES,
    Permission.MANAGE_OWN_COMPANY,
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ALL_STATS,
    Permission.VIEW_BILLING,
    Permission.VIEW_DEALS,
    Permission.VIEW_COMPANY_USAGE,
    Permission.VIEW_PROJECTS,
    Permission.MANAGE_PROJECTS
  ],
  
  [UserRole.END_USER]: [
    Permission.CREATE_TICKET,
    Permission.VIEW_OWN_TICKETS,
    Permission.UPDATE_OWN_TICKETS,
    Permission.VIEW_PROJECTS
  ]
};

type PermissionUser = {
  role: UserRole;
  canViewInvoices?: boolean;
  canViewDeals?: boolean;
  company?: {
    isOwner?: boolean;
    modules?: {
      projects?: boolean;
      projectsHidden?: boolean;
      projectsTrialStartedAt?: string;
      projectsTrialEndsAt?: string;
    };
  };
} | null | undefined;

export const userHasPermission = (user: PermissionUser, permission: Permission): boolean => {
  if (!user?.role) {
    return false;
  }
  if (!hasPermission(user.role, permission)) {
    return false;
  }
  if (permission === Permission.VIEW_BILLING && user.role === UserRole.CLIENT_ADMIN) {
    return Boolean(user.canViewInvoices);
  }
  if (permission === Permission.VIEW_DEALS && user.role === UserRole.CLIENT_ADMIN) {
    return Boolean(user.canViewDeals);
  }
  if (permission === Permission.VIEW_PROJECTS || permission === Permission.MANAGE_PROJECTS) {
    if (user.role === UserRole.SUPER_ADMIN) {
      return true;
    }
    if (user.role === UserRole.AGENT) {
      return permission === Permission.VIEW_PROJECTS;
    }
    const access = getProjectsAccess(user.company);
    if (user.role === UserRole.CLIENT_ADMIN && permission === Permission.VIEW_PROJECTS) {
      return access.enabled || access.kind === 'none';
    }
    return access.enabled;
  }
  return true;
};

/**
 * Check if a role has a specific permission
 */
export const hasPermission = (role: UserRole, permission: Permission): boolean => {
  const permissions = ROLE_PERMISSIONS[role];
  return permissions ? permissions.includes(permission) : false;
};

/**
 * Check if a role has any of the specified permissions
 */
export const hasAnyPermission = (role: UserRole, permissions: Permission[]): boolean => {
  return permissions.some(permission => hasPermission(role, permission));
};

/**
 * Check if a role has all of the specified permissions
 */
export const hasAllPermissions = (role: UserRole, permissions: Permission[]): boolean => {
  return permissions.every(permission => hasPermission(role, permission));
};

/**
 * Get all permissions for a role
 */
export const getRolePermissions = (role: UserRole): Permission[] => {
  return ROLE_PERMISSIONS[role] || [];
};

/**
 * Check if user can access a specific page
 */
export const canAccessPage = (role: UserRole, page: string): boolean => {
  const pagePermissions: Record<string, Permission[]> = {
    '/dashboard': [Permission.VIEW_DASHBOARD],
    '/tickets': [Permission.VIEW_OWN_TICKETS],
    '/tickets/new': [Permission.CREATE_TICKET],
    '/users': [Permission.VIEW_USERS],
    '/companies': [Permission.VIEW_COMPANIES],
    '/profile': [],
    '/time-tracking': [Permission.VIEW_TIME_TRACKING],
    '/usage': [Permission.VIEW_COMPANY_USAGE],
    '/invoices': [Permission.VIEW_BILLING],
    '/invoices/new': [Permission.MANAGE_BILLING],
    '/deals': [Permission.VIEW_DEALS],
    '/billing': [Permission.VIEW_BILLING],
    '/audit-logs': [Permission.VIEW_AUDIT_LOGS],
    '/projects': [Permission.VIEW_PROJECTS]
  };

  const requiredPermissions = pagePermissions[page];
  if (!requiredPermissions) return true; // Allow access if no specific permissions defined
  
  return hasAnyPermission(role, requiredPermissions);
};

